import { useAudioRecorder } from "react-audio-voice-recorder";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { createOrUpdateStorageFileToUserProfile } from "../../../../requests/StorageRequests";
import connection_production from "../../../../config/connection-production.json";
import connection_development from "../../../../config/connection-development.json";
import connection_local from "../../../../config/connection-local.json";
import endpoints from "../../../../config/endpoints.json";
import i18n from "../../../../language/i18n";
import QRCode from "react-qr-code";
import { NotificationManager } from "../../../../notifications/NotificationManager";
import {
  PrimaryButton,
  SecondaryButton,
  DividerLine,
  InputGroup,
  Icon,
  BaseModal,
  BaseTitle,
  convertDataURLtoFile,
  retrieveFileTypeFromExtension,
  resolveUploadFileType,
  compressAnswerImageFile,
  useScreenSize,
  BaseLoadingWithText,
} from "../../../../library";

import { openEndedActivityMaterialContentStyles } from "./OpenEndedActivityMaterialContentStyles";
import { oe2Styles } from "./OpenEndedActivityMaterialContentOe2Styles";
import { resolveOpenEndedSource } from "./openEndedSource";
import { blockCopyPasteEvent } from "../openEndedCopyGuard";

// Lazy so the canvas (react-konva) and PDF (pdfjs) stacks load only when the drawing modal /
// a PDF answer is actually rendered, instead of riding in every solve screen's chunk.
const Konva = lazy(() => import("../../konvas/Konva"));
const BasePDF = lazy(
  () => import("../../../../library/components/pdfs/BasePDF"),
);

// Phone-side worst case: 15 s image compression + the 90 s upload timeout, plus slack.
const PHONE_UPLOAD_WAIT_HINT_MS = 180000;

const connection =
  process.env.REACT_APP_NODE_ENV === "local"
    ? connection_local
    : process.env.REACT_APP_NODE_ENV === "development"
      ? connection_development
      : process.env.REACT_APP_NODE_ENV === "production"
        ? connection_production
        : connection_local;

interface OpenEndedActivityMaterialContentProps {
  answerMap: any;
  bodyMap: any;
  contentMap: any;
  checkCanAnswerQuestion?: any;
  onChange: (answerMap: any, value: any) => void;
  userId?: any;
  userProfileId?: any;
  // GENERIC upload source, for any surface that isn't one of the two legacy
  // assignment kinds (e.g. sourceType="SUPPORT_ASSIGNMENT" + the group id). Wins over
  // the two legacy id props — see resolveOpenEndedSource.
  sourceType?: string;
  sourceId?: any;
  branchAssignmentId?: any;
  personalizedAssignmentId?: any;
  activityId?: any;
  activityTemplateId?: any;
  curriculumType?: any;
  webSocketStompClient?: any;
  // Graded/checked view — freezes the textarea and swaps in the design's
  // "answer submitted" card (spv OpenEndedMock checked state).
  isPreview?: boolean;
  // Opt-in read-only without the submitted card (see OpenEndedActivityContent.isLocked).
  isLocked?: boolean;
}

const OpenEndedActivityMaterialContent = (
  props: OpenEndedActivityMaterialContentProps,
) => {
  const {
    answerMap,
    bodyMap,
    contentMap,
    onChange,
    userId,
    userProfileId,
    sourceType,
    sourceId,
    branchAssignmentId,
    personalizedAssignmentId,
    activityId,
    activityTemplateId,
    curriculumType,
    webSocketStompClient,
    isPreview,
    isLocked,
  } = props;
  // ONE resolution of the upload source for the whole component: checkShowContent,
  // the upload formData pair, the recording file name, the phone QR payload and the
  // websocket subscription all read it, so they can never disagree again.
  const resolvedSource = resolveOpenEndedSource({
    sourceType,
    sourceId,
    branchAssignmentId,
    personalizedAssignmentId,
  });
  // Flattened out of the object so the websocket effect's dependency array stays
  // statically checkable (react-hooks/exhaustive-deps rejects a complex expression),
  // and so it keys off the resolved pair rather than a fresh object identity.
  const resolvedSourceType = resolvedSource ? resolvedSource.sourceType : null;
  const resolvedSourceId = resolvedSource ? resolvedSource.sourceId : null;
  const { containerSize } = useScreenSize();
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadingStatus, setUploadingStatus] = useState<string | null>(null);

  const OPEN_ENDED_MAX_LENGTH = 1500;

  const {
    startRecording,
    stopRecording,
    togglePauseResume,
    recordingBlob,
    isRecording,
    isPaused,
    recordingTime,
  } = useAudioRecorder();
  const [showUploadFromPhoneModal, setShowUploadFromPhoneModal] =
    useState(false);
  // The desktop learns about a phone upload ONLY from the websocket message below. If that
  // message never comes (dropped socket, failed phone upload) the modal used to wait silently
  // forever; after this long it says so and tells the learner what to do instead.
  const [phoneUploadWaitExceeded, setPhoneUploadWaitExceeded] = useState(false);
  const inputFileRef = useRef<HTMLInputElement | null>(null);
  const konvaRef = useRef<any>(null);
  const [
    subscribedStorageFilesUserProfileFilesId,
    setStorageFilesUserProfileFilesSubscribedId,
  ] = useState<string | null>(null);
  const [showKonvaModal, setShowKonvaModal] = useState(false);
  const answerMapRef = useRef(answerMap);
  const onChangeRef = useRef(onChange);
  // Which question's "upload from phone" QR is outstanding, as `activityId:activityTemplateId`.
  // The websocket destination is per PROFILE, and the published payload carries only
  // userProfileId/fileType/fileName — no activity — so a late-arriving phone upload is otherwise
  // indistinguishable from one meant for whatever question is on screen NOW.
  const qrOpenedForRef = useRef<string | null>(null);

  useEffect(() => {
    answerMapRef.current = answerMap;
  }, [answerMap]);

  useEffect(() => {
    setPhoneUploadWaitExceeded(false);
    if (!showUploadFromPhoneModal) return;
    const timer = setTimeout(
      () => setPhoneUploadWaitExceeded(true),
      PHONE_UPLOAD_WAIT_HINT_MS,
    );
    return () => clearTimeout(timer);
  }, [showUploadFromPhoneModal]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (recordingBlob) {
      // `slug` reproduces the legacy `branch-assignment-N` / `personalized-assignment-N`
      // prefixes verbatim, so existing recordings keep their names.
      const blob = new File(
        [recordingBlob],
        // `?? "answer"` rather than a bare `?.`: the optional chain reads as guarded but would
        // interpolate the literal string "undefined" into the filename when no source resolves.
        `${resolvedSource?.slug ?? "answer"}-${activityId}-${activityTemplateId}.mp3`,
        { type: "audio/mp3" },
      );
      setUploadFile(blob);
    }
  }, [recordingBlob]);

  useEffect(() => {
    if (!uploadFile) return;
    handleUploadUserProfileFile();
  }, [uploadFile]);

  useEffect(() => {
    if (!resolvedSource || !userId || !userProfileId || !webSocketStompClient) {
      return;
    }

    const subscribed = webSocketStompClient.subscribe(
      `/${userProfileId}/storage-files/user-profile-files`,
      (message: any) => {
        let { body } = message;
        body = JSON.parse(body);
        const { fileType, fileName } = body;
        // Only accept it for the question whose QR is actually outstanding. This component instance
        // survives moving between questions (the refs below track whatever is rendered NOW), so
        // without this a phone upload started on Q3 and landing after the learner reached Q4 wrote
        // Q3's file into Q4's answer map — and the next "Kontrol et" would persist it. The upload
        // itself is unaffected; it is stored under its own (activity, template) key and shows up when
        // that question is reopened. A proper fix is for the backend to publish the source ids in
        // this payload so the message can be matched rather than inferred.
        if (
          qrOpenedForRef.current !== `${activityId}:${activityTemplateId}`
        ) {
          return;
        }
        qrOpenedForRef.current = null;
        onChangeRef.current(
          answerMapRef.current,
          `${connection.base.storage.url}${endpoints.storage.userProfile.context}${
            endpoints.storage.userProfile.preview.file.context
          }/${userId}/${userProfileId}/${fileType.toLowerCase()}s/${fileName}`,
        );
        setShowUploadFromPhoneModal(false);
      },
    );
    setStorageFilesUserProfileFilesSubscribedId(subscribed.id);
    return () => {
      webSocketStompClient.unsubscribe(subscribed.id);
      setStorageFilesUserProfileFilesSubscribedId(null);
    };
    // The handler closes over userId/userProfileId (it builds the preview URL from
    // them) — they belong in the deps, or a late-hydrating user context leaves a
    // subscription that reports a file under the wrong profile path.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    resolvedSourceType,
    resolvedSourceId,
    userId,
    userProfileId,
    activityId,
    activityTemplateId,
    webSocketStompClient,
  ]);

  const checkShowContent = () => {
    // isPreview means the answer is already committed and this render is read-only, so every mutation
    // affordance below must be inert. This has to be checked HERE rather than at each call site: `isPreview`
    // is consulted in exactly one other place (the TEXT submitted-card), while ~10 upload/record gates and
    // both async paths (the storage POST and the QR/phone websocket handler) key off showContent alone.
    // Before support assignments could resolve a source, showContent was false for them and that hid the gap;
    // now that they can, a checked open-ended question would otherwise still render live upload tiles whose
    // POST upserts the stored file and reports SUCCESS — while the follow-up onChange is dropped by the
    // wrapper's own isPreview guard. Net effect without this line: "yüklendi", answer unchanged, storage
    // row overwritten.
    if (isPreview || isLocked) {
      return false;
    }
    if (userId && userProfileId && resolvedSource) {
      return true;
    }
    return false;
  };

  const constructQRData = () => {
    const base64Data = {
      // The landing screen (FileToAnyApplicationScreen) whitelists this `type`, so an
      // unresolved source must stay empty rather than guess — the phone upload then
      // fails loudly instead of silently reporting success.
      type: resolvedSource ? resolvedSource.sourceType : "",
      applicationId: resolvedSource ? resolvedSource.sourceId : null,
      userId,
      userProfileId,
      activityId,
      activityTemplateId,
      curriculumType,
    };

    return `${connection.app.url}/upload/file-to-application/${btoa(
      encodeURI(JSON.stringify(base64Data)),
    )}`;
  };

  const MAX_FILE_SIZE_MB = 25;
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

  const handleUploadUserProfileFile = async () => {
    if (uploadFile.size > MAX_FILE_SIZE_BYTES) {
      NotificationManager.error(i18n.t("file_size_exceeds_limit"));
      setUploadFile(null);
      return;
    }

    setUploadingStatus("UPLOADING");

    // Never rejects and has its own timeout: falls back to the original file.
    const fileToUpload: File = await compressAnswerImageFile(uploadFile);

    const formData = new FormData();
    formData.append("file", fileToUpload);
    const fileType = resolveUploadFileType(fileToUpload) || "FILE";
    formData.append(
      "path",
      `/${userId}/${userProfileId}/${fileType.toLowerCase()}s`,
    );

    formData.append("fileType", fileType);
    formData.append("curriculumType", curriculumType);
    formData.append("userId", userId);
    formData.append("userProfileId", userProfileId);
    formData.append("activityId", activityId);
    formData.append("activityTemplateId", activityTemplateId);
    // No source ⇒ nothing to upload against. Previously this fell through to the
    // success path with `curData` still undefined and dereferenced `curData.fileName`
    // below (a TypeError). The upload tiles are gated on the same resolution, so this
    // is defence in depth, not a reachable flow — fail loudly, not with a crash.
    if (!resolvedSource) {
      setUploadingStatus("FAILED");
      return;
    }
    formData.append("sourceType", resolvedSource.sourceType);
    formData.append("sourceId", resolvedSource.sourceId);
    // subSourceId is a REQUIRED @RequestParam on /storage-files/user-profile/upload/file
    // (400 "Required parameter 'subSourceId' is not present." without it), and the backend
    // upserts the row on (userId, userProfileId, sourceType, sourceId, subSourceId). sourceId
    // is the assignment, so the sub-source must be the per-question id — otherwise every
    // question in one assignment would overwrite a single shared row.
    formData.append("subSourceId", activityTemplateId);
    const { data: curData, err: curErr } =
      await createOrUpdateStorageFileToUserProfile(formData);

    if (curErr || !curData) {
      setUploadingStatus("FAILED");
      return;
    }
    onChange(
      answerMap,
      `${connection.base.storage.url}${endpoints.storage.userProfile.context}${
        endpoints.storage.userProfile.preview.file.context
      }/${userId}/${userProfileId}/${fileType.toLowerCase()}s/${
        curData.fileName
      }`,
    );
    setUploadingStatus("SUCCESS");
  };

  const handleAfterCompleteOnClick = (dataUri: string) => {
    const file = convertDataURLtoFile(dataUri, `${new Date().getTime()}.jpeg`);
    setUploadFile(file);
    setShowKonvaModal(false);
  };

  const convertBodyMapToKonvaItemList = () => {
    const itemList: any[] = [];
    Object.keys(bodyMap).forEach((key) => {
      itemList.push(JSON.parse(bodyMap[key]));
    });
    return itemList;
  };

  const RenderStartRecordingContent = () => {
    if (isRecording) {
      return (
        <div
          className={`oe2-rec-box ${isPaused ? "oe2-rec-box--visible" : "oe2-rec-box--hidden"}`}
          onClick={() => {
            if (showContent) {
              togglePauseResume();
            } else {
              NotificationManager.info(
                i18n.t("operations_not_permitted_at_preview"),
              );
            }
          }}
        >
          <Icon name="audio-element-ongoing-started" className="oe2-rec-icon" />
          <p>{i18n.t("continue_recording_text")}</p>
        </div>
      );
    } else {
      return (
        <div
          className="oe2-rec-box oe2-rec-box--visible"
          onClick={() => {
            if (showContent) {
              startRecording();
            } else {
              NotificationManager.info(
                i18n.t("operations_not_permitted_at_preview"),
              );
            }
          }}
        >
          <Icon name="audio-element-not-started" className="oe2-rec-icon" />
          <p>{i18n.t("start_recording_text")}</p>
        </div>
      );
    }
  };

  const RenderListenAudioTextContent = (answerMapAnswer: any) => {
    if (!answerMapAnswer) return null;
    return (
      <div className="oe2-rec-status oe2-rec-status--blue">
        <p className="oe2-rec-status-text--2xl">
          {i18n.t("completed_recording_text")}
        </p>
      </div>
    );
  };

  const RenderRecordingTextContent = () => {
    if (isPaused) {
      return (
        <div className="oe2-rec-status oe2-rec-status--orange">
          <p className="oe2-rec-status-text--xl-orange">
            {i18n.t("paused_recording_text")}
          </p>
        </div>
      );
    } else {
      if (isRecording) {
        return (
          <div className="oe2-rec-status oe2-rec-status--red">
            <p className="oe2-rec-status-text--xl-red">
              {i18n.t("currently_recording_text")}
            </p>
          </div>
        );
      } else {
        return (
          <div className="oe2-rec-status oe2-rec-status--blue">
            <p className="oe2-rec-status-text--xl-blue">
              {i18n.t("not_started_recording_text")}
            </p>
          </div>
        );
      }
    }
  };

  const RenderStopRecordingContent = () => {
    if (isRecording) {
      return (
        <div
          className={`oe2-rec-box ${isPaused ? "oe2-rec-box--hidden" : "oe2-rec-box--visible"}`}
          onClick={() => {
            if (showContent) {
              togglePauseResume();
            } else {
              NotificationManager.info(
                i18n.t("operations_not_permitted_at_preview"),
              );
            }
          }}
        >
          <Icon name="audio-element-started" className="oe2-rec-icon" />
          <p>{i18n.t("stop_recording_text")}</p>
        </div>
      );
    }
    return null;
  };

  const RenderRecordingContent = (answerMap: any) => {
    return (
      <div className="oe2-audio-inner">
        {answerMap["ANSWER"] ? (
          <>
            {RenderListenAudioTextContent(answerMap["ANSWER"])}
            {RenderListenAudioContent(answerMap["ANSWER"])}
            <div className="oe2-audio-restart">
              <SecondaryButton
                title={i18n.t("restart")}
                size="unlimited"
                onClick={() => {
                  if (showContent) {
                    onChange(answerMap, "");
                  } else {
                    NotificationManager.info(
                      i18n.t("operations_not_permitted_at_preview"),
                    );
                  }
                }}
              />
            </div>
          </>
        ) : (
          <>
            {RenderRecordingTextContent()}
            <div className="oe2-audio-row">
              {RenderStartRecordingContent()}
              {RenderStopRecordingContent()}
            </div>
            {isRecording ? (
              <div className="oe2-rec-complete">
                <PrimaryButton
                  title={i18n.t("complete")}
                  size="small"
                  onClick={() => {
                    if (showContent) {
                      stopRecording();
                    } else {
                      NotificationManager.info(
                        i18n.t("operations_not_permitted_at_preview"),
                      );
                    }
                  }}
                />
              </div>
            ) : null}
          </>
        )}
      </div>
    );
  };

  const RenderUploadStatusTextContent = () => {
    if (uploadingStatus === "UPLOADING") {
      return (
        <div className="oe2-status">
          <div className="oe2-status-row">
            <Icon name="three-dots" size="medium" />
            <p>{i18n.t("please_wait_processing")}</p>
          </div>
        </div>
      );
    } else if (uploadingStatus === "FAILED") {
      return (
        <div className="oe2-status">
          <div className="oe2-status-row-err">
            <Icon name="cross-red" size="medium" />
            <p>{i18n.t("error_occurred_please_wait")}</p>
          </div>
          <div className="oe2-status-mt">
            <PrimaryButton
              title={i18n.t("retry_again")}
              size="small"
              onClick={handleUploadUserProfileFile}
            />
          </div>
        </div>
      );
    } else if (uploadingStatus === "SUCCESS") {
      return (
        <div className="oe2-status">
          <div className="oe2-status-row">
            <Icon name="info" size="medium" />
            <p>{i18n.t("processing_completed")}</p>
          </div>
        </div>
      );
    }
    return null;
  };

  const RenderListenAudioContent = (answerMapAnswer: any) => {
    if (!answerMapAnswer) return null;
    return (
      <div className="oe2-audio-row">
        <div style={{ flex: "1 1 0%" }}>
          <Icon name="audio-element-finished" className="oe2-rec-icon" />
        </div>
        <div style={{ flex: "1 1 0%" }}>
          {showContent ? (
            uploadingStatus === "UPLOADING" ? (
              <div className="oe2-media-center">
                <BaseLoadingWithText
                  size="medium"
                  primaryColor="#55777f"
                  secondaryColor="55777f"
                />
              </div>
            ) : uploadingStatus === "SUCCESS" ? (
              <div className="oe2-audio-col">
                {RenderUploadStatusTextContent()}
                <div className="oe2-audio-box">
                  <audio
                    className="oe2-audio"
                    src={answerMapAnswer}
                    controls
                    onClick={() => {}}
                  />
                </div>
              </div>
            ) : null
          ) : (
            <div className="oe2-audio-col">
              {RenderUploadStatusTextContent()}
              <div className="oe2-audio-box">
                <audio
                  className="oe2-audio"
                  src={answerMapAnswer}
                  controls
                  onClick={() => {}}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const RenderImageContent = (answerMap: any) => {
    const answerMapAnswer = answerMap["ANSWER"];
    let fileType;
    if (
      answerMapAnswer &&
      answerMapAnswer.includes(
        `${endpoints.storage.userProfile.context}${endpoints.storage.userProfile.preview.file.context}/`,
      )
    ) {
      const extension = answerMapAnswer.split(".").pop();
      fileType = retrieveFileTypeFromExtension(extension);
    }
    return (
      <>
        {RenderUploadFileContent(answerMapAnswer)}
        {uploadingStatus === "UPLOADING" ? (
          <div className="oe2-media-center">
            <BaseLoadingWithText
              size="medium"
              primaryColor="#55777f"
              secondaryColor="55777f"
            />
          </div>
        ) : fileType === "IMAGE" ? (
          <div className="oe2-media-center">
            {answerMapAnswer ? (
              <img
                src={answerMapAnswer}
                alt="file"
                className="oe2-media-img"
                style={{ objectFit: "contain" }}
              />
            ) : null}
          </div>
        ) : fileType === "PDF" ? (
          <div className="oe2-media-center">
            <Suspense fallback={null}>
              <BasePDF file={answerMapAnswer} />
            </Suspense>
          </div>
        ) : null}
      </>
    );
  };

  const RenderTextContent = (answerMap: any) => {
    const answerMapAnswer = answerMap["ANSWER"];
    let fileType: any = "TEXT";
    if (
      answerMapAnswer &&
      answerMapAnswer.includes(
        `${endpoints.storage.userProfile.context}${endpoints.storage.userProfile.preview.file.context}/`,
      )
    ) {
      const extension = answerMapAnswer.split(".").pop();
      fileType = retrieveFileTypeFromExtension(extension);
    }

    // Submitted / checked state — design (OpenEndedMock checked): a white card
    // with the "answer submitted, teacher/AI grades" notice; the learner's own
    // text echoes below it.
    //
    // Keyed off `isPreview` ONLY. It used to also fire on `!showContent &&
    // answerMapAnswer`, i.e. "no upload source resolved, but there is text" — which
    // collapsed a still-being-typed answer to a read-only card after the FIRST
    // keystroke on any surface without a source. Every genuinely read-only caller
    // (the /practise review screens, ActivityPreviewByData) passes isPreview={true}
    // anyway, so nothing loses the card.
    if (isPreview && fileType === "TEXT") {
      return (
        <div className="oe2-root">
          <div className="oe2-submitted">
            <span className="oe2-submitted-note">
              <span className="oe2-submitted-icon">✓</span>
              <span>{i18n.t("open_ended_submitted")}</span>
            </span>
            {answerMapAnswer ? (
              <span className="oe2-submitted-text">{answerMapAnswer}</span>
            ) : null}
          </div>
        </div>
      );
    }

    return (
      <>
        {RenderUploadFileContent(answerMapAnswer)}
        {fileType === "IMAGE" ? (
          <div className="oe2-media-center">
            {answerMapAnswer ? (
              <img
                src={answerMapAnswer}
                alt="file"
                className="oe2-media-img"
                style={{ objectFit: "contain" }}
              />
            ) : null}
          </div>
        ) : fileType === "PDF" ? (
          <div className="oe2-media-center">
            <Suspense fallback={null}>
              <BasePDF file={answerMapAnswer} />
            </Suspense>
          </div>
        ) : (
          <div className="oe2-root">
            <textarea
              className="oe2-area"
              maxLength={OPEN_ENDED_MAX_LENGTH}
              value={answerMapAnswer || ""}
              placeholder={i18n.t("open_ended_placeholder")}
              readOnly={isLocked}
              aria-readonly={isLocked || undefined}
              onChange={(e: any) => {
                if (isLocked) return;
                onChange(answerMap, e.target.value);
              }}
              // Anti-cheat speed bump (see ./openEndedCopyGuard): paste and drop are the
              // half of the loop that lands an externally-generated answer in the box;
              // copy/cut are here because the PO scoped the whole answer box.
              //
              // `drop` needs its own handler — a text DRAG into a textarea inserts the
              // text without ever firing `paste`. No `dragover` handler: a textarea is a
              // native drop target either way, and refusing the drag there
              // (dropEffect="none") would suppress the `drop` event and with it the
              // toast that explains what just did not happen.
              onPaste={blockCopyPasteEvent}
              onCopy={blockCopyPasteEvent}
              onCut={blockCopyPasteEvent}
              onDrop={blockCopyPasteEvent}
            />
            <div className="oe2-foot">
              <span>
                {(answerMapAnswer || "").length} / {OPEN_ENDED_MAX_LENGTH}{" "}
                {i18n.t("characters")}
              </span>
              <span className="oe2-foot-sep">·</span>
              <span className="oe2-hint">
                <img
                  className="oe2-hint-avatar"
                  src="/mascots/catchy-avatar.png"
                  alt=""
                  aria-hidden="true"
                />
                <span>{i18n.t("catchy_hint_open_ended")}</span>
              </span>
            </div>
          </div>
        )}
      </>
    );
  };

  const RenderUploadFileContent = (answerMapAnswer: any) => {
    let show = false;
    let exists = false;
    if (answerMapAnswer) {
      if (
        answerMapAnswer.startsWith(
          `${connection.base.storage.url}${endpoints.storage.userProfile.context}${endpoints.storage.userProfile.preview.file.context}/${userId}/${userProfileId}`,
        )
      ) {
        show = true;
        exists = true;
      }
    } else {
      show = true;
      exists = false;
    }
    if (!show) return null;

    return (
      <div className="oe2-upload-row">
        <div
          className="oe2-upload-option"
          onClick={() => {
            if (showContent) {
              setUploadFile(null);
              inputFileRef.current.click();
            } else {
              NotificationManager.info(
                i18n.t("operations_not_permitted_at_preview"),
              );
            }
          }}
        >
          <Icon name="upload-pc" size="small" />
          <p>
            {exists
              ? i18n.t("i_want_to_reupload_from_my_computer")
              : i18n.t("i_want_to_upload_from_my_computer")}
          </p>
          <input
            className="oe2-hidden"
            type="file"
            id="file"
            ref={inputFileRef}
            accept="image/jpeg, image/png, image/jpg, application/pdf"
            multiple={false}
            onChange={(event) => {
              setUploadFile(event.target.files[0]);
            }}
            onClick={(event: any) => {
              event.target.value = null;
            }}
          />
        </div>
        <div
          className="oe2-upload-option"
          onClick={() => {
            if (showContent) {
              // Remember WHICH question the QR was opened for — see the websocket handler.
              qrOpenedForRef.current = `${activityId}:${activityTemplateId}`;
              setShowUploadFromPhoneModal(true);
            } else {
              NotificationManager.info(
                i18n.t("operations_not_permitted_at_preview"),
              );
            }
          }}
        >
          <Icon name="upload-device" size="small" />
          <p>
            {exists
              ? i18n.t("i_want_to_reupload_from_my_phone")
              : i18n.t("i_want_to_upload_from_my_phone")}
          </p>
        </div>
        <div
          className="oe2-upload-option"
          onClick={() => {
            if (showContent) {
              setShowKonvaModal(true);
            } else {
              NotificationManager.info(
                i18n.t("operations_not_permitted_at_preview"),
              );
            }
          }}
        >
          <Icon name="upload-draw" size="small" />
          <p>
            {exists
              ? i18n.t("i_want_to_reupload_by_drawing")
              : i18n.t("i_want_to_upload_by_drawing")}
          </p>
        </div>
      </div>
    );
  };

  const RenderUploadFromPhoneModal = () => {
    return (
      <BaseModal
        title={i18n.t("upload_from_phone")}
        icon="📲"
        isOpen={showUploadFromPhoneModal}
        size="medium"
        onAfterOpen={() => {}}
        onRequestClose={() => {
          setShowUploadFromPhoneModal(false);
        }}
      >
        <div className="oe2-modal-row">
          <div className="oe2-modal-half">
            <div className="oe2-qr-pad">
              <QRCode
                size={256}
                value={constructQRData()}
                viewBox={`0 0 256 256`}
                level="L"
              />
            </div>
          </div>
          <div style={{ flex: "1 1 0%" }}>
            <BaseTitle title={i18n.t("instructions")} />
            <div className="oe2-instructions-pad">
              <div className="oe2-instructions-list">
                <div><p>1. {i18n.t("open_camera_from_your_mobile_phone")}</p></div>
                <div><p>2. {i18n.t("read_the_qr_code")}</p></div>
                <div><p>3. {i18n.t("navigate_to_web_page")}</p></div>
                <div><p>4. {i18n.t("upload_your_file")}</p></div>
                <div><p>5. {i18n.t("wait_until_the_file_is_uploaded")}</p></div>
                <div><p>6. {i18n.t("close_panel")}</p></div>
              </div>
              {phoneUploadWaitExceeded && (
                <p className="oe2-phone-wait-hint" role="status">
                  {i18n.t("phone_upload_not_received_hint")}
                </p>
              )}
            </div>
          </div>
        </div>
      </BaseModal>
    );
  };

  const RenderConvaContent = () => {
    return (
      <BaseModal
        title={i18n.t("drawing_tool")}
        icon="🎨"
        isOpen={showKonvaModal}
        onAfterOpen={() => {}}
        size="medium"
        onRequestClose={() => {
          if (konvaRef.current?.hasDrawings()) {
            if (
              !window.confirm(
                i18n.t("are_you_sure_do_you_want_to_close_drawing_text"),
              )
            )
              return;
          }
          setShowKonvaModal(false);
        }}
      >
        {(() => {
          const KonvaAny = Konva as any;
          return (
            <Suspense fallback={null}>
              <KonvaAny
                ref={konvaRef}
                initialItemList={convertBodyMapToKonvaItemList()}
                learnerAnswer={answerMap?.["ANSWER"]}
                initialWidth={containerSize.width}
                handleAfterCompleteOnClick={handleAfterCompleteOnClick}
              />
            </Suspense>
          );
        })()}
      </BaseModal>
    );
  };

  const inputVideoFileRef = useRef<HTMLInputElement | null>(null);

  const RenderVideoContent = (answerMap: any) => {
    const answerMapAnswer = answerMap["ANSWER"];
    let fileType;
    if (
      answerMapAnswer &&
      answerMapAnswer.includes(
        `${endpoints.storage.userProfile.context}${endpoints.storage.userProfile.preview.file.context}/`,
      )
    ) {
      const extension = answerMapAnswer.split(".").pop();
      fileType = retrieveFileTypeFromExtension(extension);
    }
    let show = false;
    let exists = false;
    if (answerMapAnswer) {
      if (
        answerMapAnswer.startsWith(
          `${connection.base.storage.url}${endpoints.storage.userProfile.context}${endpoints.storage.userProfile.preview.file.context}/${userId}/${userProfileId}`,
        )
      ) {
        show = true;
        exists = true;
      }
    } else {
      show = true;
      exists = false;
    }
    return (
      <>
        {show ? (
          <div className="oe2-upload-row">
            <div
              className="oe2-upload-option"
              onClick={() => {
                if (showContent) {
                  setUploadFile(null);
                  inputVideoFileRef.current.click();
                } else {
                  NotificationManager.info(
                    i18n.t("operations_not_permitted_at_preview"),
                  );
                }
              }}
            >
              <Icon name="upload-pc" size="small" />
              <p>
                {exists
                  ? i18n.t("i_want_to_reupload_from_my_computer")
                  : i18n.t("i_want_to_upload_from_my_computer")}
              </p>
              <input
                className="oe2-hidden"
                type="file"
                id="video-file"
                ref={inputVideoFileRef}
                accept="video/mp4, video/webm, video/quicktime, video/x-msvideo"
                multiple={false}
                onChange={(event) => {
                  setUploadFile(event.target.files[0]);
                }}
                onClick={(event: any) => {
                  event.target.value = null;
                }}
              />
            </div>
          </div>
        ) : null}
        {uploadingStatus === "UPLOADING" ? (
          <div className="oe2-media-center">
            <BaseLoadingWithText
              size="medium"
              primaryColor="#57C2D3"
              secondaryColor="#57C2D3"
            />
          </div>
        ) : fileType === "VIDEO" ? (
          <div className="oe2-media-center">
            <video
              className="oe2-media-video"
              src={answerMapAnswer}
              controls
            />
          </div>
        ) : null}
      </>
    );
  };

  const showContent = checkShowContent();

  return (
    <>
      <style>{openEndedActivityMaterialContentStyles}</style>
      <style>{oe2Styles}</style>
      {RenderConvaContent()}
      {RenderUploadFromPhoneModal()}
      {contentMap.type === "TEXT" ? (
        RenderTextContent(answerMap)
      ) : contentMap.type === "IMAGE" ? (
        RenderImageContent(answerMap)
      ) : contentMap.type === "AUDIO" ? (
        <div className="oe2-audio-center">
          {RenderRecordingContent(answerMap)}
        </div>
      ) : contentMap.type === "VIDEO" ? (
        RenderVideoContent(answerMap)
      ) : null}
    </>
  );
};

export default OpenEndedActivityMaterialContent;
